/**
 * Shared event helpers: batch_type from batch config.
 * For experiment_name use the value from common-batch-listing or course-comparison API responses (experimentName).
 */

import { BatchTypeForEvent, EventType, type BatchPageVisitBatchType } from '@/constants/events';

const CJR_TO_BATCH_TYPE: Record<string, BatchPageVisitBatchType> = {
  booster: BatchTypeForEvent.Booster,
  master: BatchTypeForEvent.Master,
  power: BatchTypeForEvent.Power,
  pocket: BatchTypeForEvent.PocketCourse,
  mega: BatchTypeForEvent.Mega,
};

/**
 * Maps API cjrBatchType (e.g. from batch.config.cjrBatchType) to event batch_type.
 * Defaults to master when unknown.
 */
export function getBatchTypeFromCjrBatchType(
  cjrBatchType: string | undefined
): BatchPageVisitBatchType {
  if (!cjrBatchType) return BatchTypeForEvent.Master;
  const key = cjrBatchType.toLowerCase();
  return CJR_TO_BATCH_TYPE[key] ?? BatchTypeForEvent.Master;
}

const BATCH_TYPE_TO_TRANSACTION_SUCCESS_EVENT: Record<string, EventType> = {
  [BatchTypeForEvent.Booster]: EventType.TRANSACTION_SUCCESS_BOOSTER,
  [BatchTypeForEvent.Master]: EventType.TRANSACTION_SUCCESS_MASTER,
  [BatchTypeForEvent.Mega]: EventType.TRANSACTION_SUCCESS_MEGA,
  [BatchTypeForEvent.Power]: EventType.TRANSACTION_SUCCESS_POWER,
  [BatchTypeForEvent.PocketCourse]: EventType.TRANSACTION_SUCCESS_POCKET,
  pocket: EventType.TRANSACTION_SUCCESS_POCKET,
};

/**
 * Maps event batch_type to the batch-specific transaction_success GA event.
 * Unknown/missing values keep the previous fallback (master).
 */
export function getBatchTransactionSuccessEventName(batchType?: string): EventType {
  if (!batchType) return EventType.TRANSACTION_SUCCESS_MASTER;
  const key = batchType.toLowerCase();
  return BATCH_TYPE_TO_TRANSACTION_SUCCESS_EVENT[key] ?? EventType.TRANSACTION_SUCCESS_MASTER;
}

/** Meta Pixel batch-type event names. Does not replace transaction_success. */
export function getBatchTransactionSuccessPixelEventName(batchType?: string): string {
  const key = (batchType || '').toLowerCase();
  switch (key) {
    case 'booster':
      return 'booster_transaction_success';
    case 'mega':
      return 'mega_transaction_success';
    case 'power':
      return 'power_transaction_success';
    case 'pocket':
    case 'pocket_course':
      return 'pocket_transaction_success';
    default:
      return 'master_transaction_success';
  }
}

/** Maps journey track state to track_status for track_content_click / track_content_open. */
export function getTrackStatusForEvent(state?: string): 'unlocked' | 'locked' {
  return state === 'locked' ? 'locked' : 'unlocked';
}

/** Unlock time for locked tracks (ISO datetime string). */
export function getTrackUnlockInForEvent(
  state?: string,
  unlockedAt?: string | null
): string | undefined {
  return state === 'locked' && unlockedAt ? unlockedAt : undefined;
}
